/**
 * MoreNotificationsRow — the "More updates / collapse" footer row of the Inbox
 * notifications section.
 *
 * Flutter source of truth: `MoreNotificationsWidget`
 * (`lib/presentation/screens/messages/widgets/more_notifications_widget.dart:27-80`).
 *
 * Visibility (mirrors Flutter exactly):
 *   - Nothing when `!hasItems`.
 *   - Else, when `hasMoreItems || canCollapse`, a centered region: a spinner
 *     while `isFetching`, else a tappable "More updates" text while
 *     `hasMoreItems` (else nothing).
 *   - The trailing up-arrow collapse button appears ONLY when `canCollapse`
 *     (tap → `onCollapse`).
 *
 * `canCollapse` is the hook-owned `notifications.length > NOTIFICATIONS_LIST_PAGINATION`
 * business rule (Flutter `itemsCount > NOTIFICATIONS_LIST_PAGINATION`), threaded
 * in as a prop so this presentation component holds no pagination knowledge.
 *
 * Layout mirrors the existing web SearchPage "more-people" row (the web
 * equivalent of this Flutter widget): a left spacer column, a flex-centered
 * middle, and a right column holding the collapse arrow (or an equal-width
 * spacer when the arrow is hidden) so the middle text stays centered.
 *
 * Token mapping (Flutter `Row` with `Container(width: 50)` side columns, padding
 * `left:16, right:16`, `notificationsLoadMoreStyle`: `AppColors.more`/blueAccent,
 * size 14, weight 400; arrow `upArrowActionSize` = 24):
 *   - side-column width 50 → `responsive.avatar.medium` (40), matching the web
 *     SearchPage "more-people" row's side-column convention.
 *   - padding 16           → `responsive.spacing.lg`.
 *   - "More updates" text → rendered via the shared `ExpauseButton` with
 *     `ButtonVariant.text`, whose default color is `theme.blue` (the project's
 *     "blueAccent" mapping), matching Flutter's `notificationsLoadMoreStyle`
 *     color `AppColors.more`/blueAccent.
 *   - size 14              → `responsive.font.sm`.
 *   - arrow size 24        → `responsive.icon.large`.
 *   - arrow color          → `theme.text.secondary` (Flutter `disabledTextColor`).
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface MoreNotificationsRowProps {
  hasMoreItems: boolean;
  hasItems: boolean;
  isFetching: boolean;
  /**
   * True when more than one page of notifications is currently expanded, i.e.
   * the collapse arrow should be offered. The pagination-threshold business
   * rule is hook-owned (`useInbox.ts`); this component only consumes the flag.
   */
  canCollapse: boolean;
  onMore: () => void;
  onCollapse: () => void;
}

export const MoreNotificationsRow: React.FC<MoreNotificationsRowProps> = ({
  hasMoreItems,
  hasItems,
  isFetching,
  canCollapse,
  onMore,
  onCollapse,
}) => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter: nothing when `!hasItems`, and nothing unless there is more to load
  // or more than a page is currently expanded.
  if (!hasItems) return null;
  if (!hasMoreItems && !canCollapse) return null;

  const sideColumnWidth = responsive.avatar.medium;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
      }}
    >
      {/* Left spacer column (Flutter `Container(width: 50)`). */}
      <div style={{ width: sideColumnWidth, flexShrink: 0 }} />

      {/* Centered middle: spinner while fetching, else tappable "More updates". */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
        {isFetching ? (
          <ExpauseLoadingIndicator />
        ) : hasMoreItems ? (
          <ExpauseButton
            {...qaAttr({ id: 'inbox-more-updates' })}
            variant={ButtonVariant.text}
            onClick={onMore}
            style={{
              fontSize: responsive.font.sm,
              fontWeight: responsive.fontWeight.regular,
              textAlign: 'center',
            }}
          >
            {l10n.inbox.moreUpdates}
          </ExpauseButton>
        ) : null}
      </div>

      {/* Right column: collapse arrow only when more than a page is expanded. */}
      {canCollapse ? (
        <div
          {...qaAttr({ id: 'inbox-collapse-updates' })}
          onClick={onCollapse}
          style={{
            width: sideColumnWidth,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <AssetIcon
            path="/assets/images/arrow_up.png"
            width={responsive.icon.large}
            height={responsive.icon.large}
            color={theme.text.secondary}
          />
        </div>
      ) : (
        <div style={{ width: sideColumnWidth, flexShrink: 0 }} />
      )}
    </div>
  );
};
